import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Store, ShoppingCart, TrendingUp, DollarSign, Copy, Clock, CheckCircle, XCircle, Edit
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => 'Rp ' + (n || 0).toLocaleString('id-ID');

export default function CustomerReseller() {
  const [reseller, setReseller] = useState(null);
  const [tier, setTier] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [editing, setEditing] = useState(false);

  // Registration form
  const [regForm, setRegForm] = useState({
    store_name: '', store_address: '', phone: '',
    bank_name: '', bank_account_number: '', bank_account_name: ''
  });

  // Profile edit form
  const [profileForm, setProfileForm] = useState({});

  const fetchReseller = useCallback(async () => {
    try {
      const { data } = await api.get('/customer/reseller');
      setReseller(data.reseller);
      setTier(data.tier);
      if (data.reseller && data.reseller.status === 'approved') {
        fetchOrders();
      }
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await api.get('/customer/reseller/orders?limit=20');
      setOrders(data.orders);
      setOrdersTotal(data.total);
    } catch {}
  };

  useEffect(() => { fetchReseller(); }, [fetchReseller]);

  const handleRegister = async () => {
    if (!regForm.store_name.trim()) {
      toast.error('Nama toko wajib diisi');
      return;
    }
    setRegistering(true);
    try {
      const { data } = await api.post('/customer/reseller/register', regForm);
      setReseller(data);
      toast.success(data.status === 'approved' ? 'Pendaftaran berhasil! Anda langsung disetujui.' : 'Pendaftaran terkirim! Menunggu persetujuan tenant.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mendaftar');
    }
    setRegistering(false);
  };

  const handleSaveProfile = async () => {
    try {
      const updated = await api.put('/customer/reseller/profile', profileForm);
      setReseller(updated.data);
      setEditing(false);
      toast.success('Profil diupdate');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal update');
    }
  };

  const copyCode = () => {
    if (reseller?.code) {
      navigator.clipboard.writeText(reseller.code);
      toast.success('Kode reseller disalin!');
    }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Memuat...</div>;

  // ── Not registered → Show registration form ──
  if (!reseller) {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Daftar sebagai Reseller</h1>
        <Card>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Menjadi reseller untuk mendapatkan harga khusus dan keuntungan lebih!
            </p>
            <div className="space-y-3">
              <div><Label>Nama Toko *</Label><Input value={regForm.store_name} onChange={e => setRegForm({ ...regForm, store_name: e.target.value })} placeholder="Nama toko reseller Anda" /></div>
              <div><Label>Alamat Toko</Label><Input value={regForm.store_address} onChange={e => setRegForm({ ...regForm, store_address: e.target.value })} placeholder="Alamat untuk pengiriman dropship" /></div>
              <div><Label>No. Telepon</Label><Input value={regForm.phone} onChange={e => setRegForm({ ...regForm, phone: e.target.value })} placeholder="08xxxxxxxxxx" /></div>
              <div className="border-t pt-3">
                <Label className="text-sm font-medium text-muted-foreground">Informasi Bank (opsional)</Label>
              </div>
              <div><Label>Nama Bank</Label><Input value={regForm.bank_name} onChange={e => setRegForm({ ...regForm, bank_name: e.target.value })} placeholder="BCA, BNI, Mandiri..." /></div>
              <div><Label>No. Rekening</Label><Input value={regForm.bank_account_number} onChange={e => setRegForm({ ...regForm, bank_account_number: e.target.value })} /></div>
              <div><Label>Nama Pemilik Rekening</Label><Input value={regForm.bank_account_name} onChange={e => setRegForm({ ...regForm, bank_account_name: e.target.value })} /></div>
            </div>
            <Button className="w-full" onClick={handleRegister} disabled={registering}>
              {registering ? 'Mendaftar...' : 'Daftar sebagai Reseller'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Pending status ──
  if (reseller.status === 'pending') {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Status Reseller</h1>
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <Clock className="w-16 h-16 text-yellow-500 mx-auto" />
            <h2 className="text-xl font-semibold">Menunggu Persetujuan</h2>
            <p className="text-muted-foreground">Pendaftaran reseller Anda sedang ditinjau oleh admin. Kami akan memberi tahu Anda setelah disetujui.</p>
            <div className="p-3 bg-muted rounded-md text-sm">
              <div>Nama Toko: <strong>{reseller.store_name}</strong></div>
              <div>Kode: <strong className="font-mono">{reseller.code}</strong></div>
              <div>Didaftarkan: {new Date(reseller.created_at).toLocaleDateString('id-ID')}</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Rejected status ──
  if (reseller.status === 'rejected') {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Status Reseller</h1>
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h2 className="text-xl font-semibold">Pendaftaran Ditolak</h2>
            <p className="text-muted-foreground">Maaf, pendaftaran reseller Anda ditolak. {reseller.notes ? `Alasan: ${reseller.notes}` : 'Hubungi admin untuk informasi lebih lanjut.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Approved → Dashboard ──
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Dashboard Reseller</h1>
        <Badge className="bg-green-100 text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 mr-1" /> Aktif
        </Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Store className="w-4 h-4" /> Tier</div>
          <div className="text-xl font-bold mt-1">{reseller.tier_name || '-'}</div>
          {tier && <div className="text-xs text-muted-foreground">Diskon {tier.discount_type === 'percentage' ? `${tier.discount_value}%` : fmt(tier.discount_value)}</div>}
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><ShoppingCart className="w-4 h-4" /> Total Order</div>
          <div className="text-xl font-bold mt-1">{reseller.total_orders}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><DollarSign className="w-4 h-4" /> Total Belanja</div>
          <div className="text-xl font-bold mt-1">{fmt(reseller.total_spent)}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4" /> Total Margin</div>
          <div className="text-xl font-bold mt-1 text-green-600">{fmt(reseller.total_margin)}</div>
        </CardContent></Card>
      </div>

      {/* Reseller Code */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-muted-foreground">Kode Reseller</Label>
              <div className="text-2xl font-mono font-bold">{reseller.code}</div>
              <p className="text-xs text-muted-foreground">Gunakan kode ini saat checkout untuk mendapatkan harga reseller</p>
            </div>
            <Button variant="outline" size="sm" onClick={copyCode}><Copy className="w-4 h-4 mr-1" /> Salin</Button>
          </div>
        </CardContent>
      </Card>

      {/* Store Info */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Informasi Toko</CardTitle>
          {!editing && <Button size="sm" variant="outline" onClick={() => { setProfileForm({ store_name: reseller.store_name, store_address: reseller.store_address, phone: reseller.phone, bank_name: reseller.bank_name, bank_account_number: reseller.bank_account_number, bank_account_name: reseller.bank_account_name }); setEditing(true); }}><Edit className="w-4 h-4 mr-1" /> Edit</Button>}
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="space-y-3">
              <div><Label>Nama Toko</Label><Input value={profileForm.store_name || ''} onChange={e => setProfileForm({ ...profileForm, store_name: e.target.value })} /></div>
              <div><Label>Alamat Toko</Label><Input value={profileForm.store_address || ''} onChange={e => setProfileForm({ ...profileForm, store_address: e.target.value })} /></div>
              <div><Label>Telepon</Label><Input value={profileForm.phone || ''} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} /></div>
              <div><Label>Nama Bank</Label><Input value={profileForm.bank_name || ''} onChange={e => setProfileForm({ ...profileForm, bank_name: e.target.value })} /></div>
              <div><Label>No. Rekening</Label><Input value={profileForm.bank_account_number || ''} onChange={e => setProfileForm({ ...profileForm, bank_account_number: e.target.value })} /></div>
              <div><Label>Nama Pemilik</Label><Input value={profileForm.bank_account_name || ''} onChange={e => setProfileForm({ ...profileForm, bank_account_name: e.target.value })} /></div>
              <div className="flex gap-2">
                <Button onClick={handleSaveProfile}>Simpan</Button>
                <Button variant="outline" onClick={() => setEditing(false)}>Batal</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Nama Toko:</span> <strong>{reseller.store_name}</strong></div>
              <div><span className="text-muted-foreground">Telepon:</span> {reseller.phone || '-'}</div>
              <div className="col-span-2"><span className="text-muted-foreground">Alamat:</span> {reseller.store_address || '-'}</div>
              {reseller.bank_name && (
                <div className="col-span-2"><span className="text-muted-foreground">Bank:</span> {reseller.bank_name} - {reseller.bank_account_number} ({reseller.bank_account_name})</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Orders */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Order Terakhir</CardTitle></CardHeader>
        <CardContent>
          {orders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left">
                  <th className="py-2 px-2">No. Order</th>
                  <th className="py-2 px-2 text-right">Total</th>
                  <th className="py-2 px-2 text-right">Margin</th>
                  <th className="py-2 px-2">Dropship</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Tanggal</th>
                </tr></thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id} className="border-b hover:bg-muted/50">
                      <td className="py-2 px-2 font-mono text-xs">{o.order_number}</td>
                      <td className="py-2 px-2 text-right">{fmt(o.total)}</td>
                      <td className="py-2 px-2 text-right text-green-600">{fmt(o.reseller_margin || 0)}</td>
                      <td className="py-2 px-2">
                        {o.is_dropship ? (
                          <Badge className="bg-blue-100 text-blue-700">Dropship</Badge>
                        ) : '-'}
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant="outline">{o.status}</Badge>
                      </td>
                      <td className="py-2 px-2 text-xs">{new Date(o.created_at).toLocaleDateString('id-ID')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-6 text-muted-foreground">Belum ada order. Mulai belanja dengan harga reseller!</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
